import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { PlanarGraph } from "geom/planarGraph";
import { addEntityIntervalsToWallIntervalLoop } from "./wallIntervalEntities";
import { vec2SequenceAngleCW } from "./wallIntervalLoopUtils";
import { traverseSolidWallsInPlanarGraph } from "./wallTraversePlanarGraph";
import type { WallInterval } from "./wallTraverseTypes";

interface TraverseSolidWallEdgesOptions {
  readonly planarGraph: PlanarGraph;
  readonly wallSystem: WallSystem;
  readonly wallMassSystem: WallMassSystem;
  readonly initialHalfEdgeIndex: number;

  readonly avoidWindows?: boolean;
  readonly avoidDoors?: boolean;
  readonly avoidDoorSwings?: boolean;

  readonly stopAtNextDoor?: boolean;
  readonly stopAtFirstPlacement?: boolean;
  readonly perimeterTravelLimit?: number;

  readonly minimumIntervalWidth?: number;
  readonly profileSource: ActiveProfileSource;
  readonly doorSettings: DoorSettings;
}

interface TraverseSolidWallEdgesResult {
  readonly collinearJoinedVertices: number[];
  readonly loop: WallInterval[][];
  readonly halfEdges: { [halfEdgeIndex: number]: boolean };
}

/**
 * Construct a wall interval loop from a given half edge index.
 * 1. Traverse the solid walls in a planar graph starting at a given half edge index.
 * 2. Determine the angle of the turn at the end of each edge so we can later consider collinear walls as a single interval.
 * 3. Mark which edges from the original planar graph are collinear with each other.
 * 4. Add the entities from Solid Walls (doors, windows, wall cuts, etc.) to the wall interval loop.
 */
export function makeWallIntervalLoop({
  planarGraph,
  wallSystem,
  wallMassSystem,
  initialHalfEdgeIndex,
  profileSource,
  doorSettings,
}: TraverseSolidWallEdgesOptions): TraverseSolidWallEdgesResult {
  const { vertices, edgeIndexToHalfEdgeIndex, halfEdges } = traverseSolidWallsInPlanarGraph({
    planarGraph,
    wallMassSystem,
    initialHalfEdgeIndex,
  });

  // determine the angle of the turn at the end of each edge
  const edgeIndexToTurnAngleAtEnd: number[] = makeEdgeIndexToTurnAngleAtEndArr(vertices);

  // create lookup of edge index to collinear edges indices
  const collinearEdgeIndices: number[][] = makeCollinearEdgeIndicesArr(edgeIndexToTurnAngleAtEnd);

  const { collinearJoinedVertices, intervals } = addEntityIntervalsToWallIntervalLoop({
    collinearEdgeIndices,
    vertices,
    edgeIndexToHalfEdgeIndex,
    wallSystem,
    profileSource,
    doorSettings,
  });

  return { collinearJoinedVertices, loop: intervals, halfEdges };
}

function makeEdgeIndexToTurnAngleAtEndArr(vertices: number[]) {
  const edgeIndexToTurnAngleAtEnd: number[] = [];
  const vlen = vertices.length;
  const wrapLen = vlen - 4;
  for (let i = 0, l = vlen - 2; i < l; i += 2) {
    const x0 = vertices[i];
    const y0 = vertices[i + 1];

    const wrap = i === wrapLen;

    const x1 = vertices[i + 2];
    const y1 = vertices[i + 3];

    const x2 = wrap ? vertices[2] : vertices[i + 4];
    const y2 = wrap ? vertices[3] : vertices[i + 5];

    edgeIndexToTurnAngleAtEnd[i / 2] = vec2SequenceAngleCW(x0, y0, x1, y1, x2, y2);
  }
  return edgeIndexToTurnAngleAtEnd;
}

export function makeCollinearEdgeIndicesArr(edgeIndexToTurnAngleAtEnd: number[]) {
  const edgeIndexToCollinearEdges: number[][] = [];
  let currentCollinears: number[] | undefined;
  for (let i = 0, l = edgeIndexToTurnAngleAtEnd.length; i < l; i++) {
    const turnAngleAtEnd = edgeIndexToTurnAngleAtEnd[i];

    if (currentCollinears === undefined) {
      currentCollinears = [];
    }
    currentCollinears.push(i);

    if (turnAngleAtEnd) {
      // cap off the group and empty collinears
      edgeIndexToCollinearEdges.push([...currentCollinears]);
      currentCollinears = [];
    } else {
      if (i === l - 1) {
        const newUpdatedCollinears = [...currentCollinears, ...edgeIndexToCollinearEdges[0]];

        edgeIndexToCollinearEdges[0] = newUpdatedCollinears;
      }
    }
  }
  return edgeIndexToCollinearEdges;
}
